import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, tap } from 'rxjs/operators';
import {
  LoginOAuthParams,
  MethodSelectionPayload,
  SubmitSmsMethod,
  SubmitSmsNumber,
  SubmitSmsOtp
} from '../../../models';
import { SubmitSmsMethodSelectionService } from '../../multi-factor-authentication-adapter-service/submit-method-selection/submit-sms-method-selection/submit-sms-method-selection.service';
import { SubmitSmsNumberService } from '../../multi-factor-authentication-adapter-service/submit-sms-number/submit-sms-number.service';
import { SubmitSMSActivationService } from '../../multi-factor-authentication-adapter-service/submit-sms-otp/submit-sms-activation.service';
import { MfaUtilService } from '../../multi-factor-authentication-adapter-service/util-service/mfa-util.service';

@Injectable({
  providedIn: 'root'
})
export class SmsMethodService {
  authCode = '';

  constructor(
    private smsMethod: SubmitSmsMethodSelectionService,
    private smsNumber: SubmitSmsNumberService,
    private smsOTP: SubmitSMSActivationService,
    private mfaUtils: MfaUtilService
  ) {}

  initSMS(
    loginOAuthParams: LoginOAuthParams,
    flowdId: string,
    otp?: string,
    phoneNumber?: string,
    retry?: boolean | undefined
  ): Observable<string | SubmitSmsNumber | SubmitSmsOtp> {
    if (phoneNumber) {
      return this.submitSmsSelection(
        loginOAuthParams,
        flowdId,
        phoneNumber,
        retry
      );
    } else {
      return this.submitSmsOtp(loginOAuthParams, flowdId, otp ? otp : '');
    }
  }

  submitSmsSelection(
    loginOAuthParams: LoginOAuthParams,
    flowdId: string,
    phoneNumber: string,
    retry: boolean | undefined
  ): Observable<string | SubmitSmsNumber> {
    if (retry) {
      return this.submitSmsNumber(loginOAuthParams, flowdId, phoneNumber);
    }

    const deviceTypeObject: MethodSelectionPayload = {
      devicePairingMethod: {
        deviceType: 'SMS'
      }
    };

    return this.smsMethod
      .submitSmsMethodSelection(
        loginOAuthParams.oauthConfig,
        flowdId,
        deviceTypeObject
      )
      .pipe(
        concatMap((res: SubmitSmsMethod) => {
          return this.smsNumber.SubmitSMSNumber(
            loginOAuthParams.oauthConfig,
            res?.id ? res.id : '',
            phoneNumber
          );
        }),
        catchError((error) => {
          return of(this.mfaUtils.getMfaRegistrationError(error));
        })
      );
  }

  submitSmsNumber(
    loginOAuthParams: LoginOAuthParams,
    flowdId: string,
    phoneNumber: string
  ): Observable<string | SubmitSmsNumber> {
    return this.smsNumber
      .SubmitSMSNumber(loginOAuthParams.oauthConfig, flowdId, phoneNumber)
      .pipe(
        catchError((error) => {
          return of(this.mfaUtils.getMfaRegistrationError(error));
        })
      );
  }

  submitSmsOtp(
    loginOAuthParams: LoginOAuthParams,
    flowdId: string,
    otp: string
  ): Observable<string | SubmitSmsOtp> {
    return this.smsOTP
      .SubmitSMSActivation(loginOAuthParams.oauthConfig, flowdId, otp)
      .pipe(
        tap((res: SubmitSmsOtp) => {
          if (res.status === 'COMPLETED') {
            this.authCode = res.authorizeResponse?.code ?? '';
          }
        }),
        catchError((error) => {
          return of(this.mfaUtils.getMfaRegistrationError(error));
        })
      );
  }
}
